import PropTypes from 'prop-types';
import React from 'react';
import SearchFilters from 'search/components/SearchFilters';
import Spinner from 'common/components/Spinner';
import SearchResults from 'search/components/SearchResults';

import SearchParamsStore from 'search/stores/ParamsStore';
import SearchResultsStore from 'search/stores/ResultsStore';

import SearchAPI from 'search/api/searchAPI';
import SearchAPIActionCreators from 'search/actions/SearchAPIActionCreators';

import satisfiesMediaQuery from 'utils/satisfiesMediaQuery';

import DocumentTitle from 'react-document-title';

import cx from 'classnames';

const getStateFromStores = () => {
  return {
    params: SearchParamsStore.get(),
    isLoading: SearchResultsStore.isLoading(),
    results: SearchResultsStore.get(),
    activeResultId: SearchResultsStore.activeResultId()
  };
};

class SearchApp extends React.Component {
  static propTypes = {
    initialParams: PropTypes.shape({
      et: PropTypes.number,
      people: PropTypes.string,
      people_filter: PropTypes.string,
      location: PropTypes.string,
      vs: PropTypes.array,
      title: PropTypes.title,
      sw: PropTypes.array,
      ne: PropTypes.array
    }).isRequired,
    initialResults: PropTypes.shape({
      meta: PropTypes.object.isRequired,
      venues: PropTypes.array.isRequired
    }).isRequired,
    capacityRanges: PropTypes.array.isRequired,
    eventTypes: PropTypes.array.isRequired,
    venueServices: PropTypes.array.isRequired,
    venueTypes: PropTypes.array.isRequired,
    isMobile: PropTypes.bool,
    isTablet: PropTypes.bool,
    features: PropTypes.shape({
      edit_relevance: PropTypes.bool
    }).isRequired
  };

  componentWillMount() {
    SearchAPIActionCreators.setInitialResults(this.props.initialResults);
    SearchAPIActionCreators.setInitialParams(this.props.initialParams);

    // Keep this value out of `this.state` as it is not used in `render`
    this.isLazyLoadingMapCode = false;

    this.setState(
      Object.assign({}, getStateFromStores(), {
        isFiltersVisible: !this.props.isMobile,
        isSmallScreen: !!this.props.isMobile,
        isMapboxLoaded: false,
        MapSearch: null
      })
    );
  }

  componentDidMount() {
    // Boot up SearchAPI's History API etc. stuff
    SearchAPI.initialize();

    window.addEventListener('resize', this._handleResize);
    this._handleResize();

    SearchParamsStore.addChangeListener(this._onChange);
    SearchResultsStore.addChangeListener(this._onChange);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._handleResize);
    SearchParamsStore.removeChangeListener(this._onChange);
    SearchResultsStore.removeChangeListener(this._onChange);
  }

  render() {
    const highlightCapacitySitting = !this.state.params.people_filter;

    const showFiltersButtonClasses = cx({
      expand: true,
      success: true,
      small: true,
      'is-toggled': this.state.isFiltersVisible
    });

    const showSearchFilters =
      this.state.isFiltersVisible || !this.state.isSmallScreen;
    const openInNewTab = !this.props.isMobile && !this.props.isTablet;

    const MapSearch = this.state.MapSearch;

    return (
      <div id="venues">
        <DocumentTitle title={this.state.results.meta.title} />
        {this.state.isSmallScreen && (
          <div className="m-mobile-filter-container">
            <button
              className={showFiltersButtonClasses}
              onClick={this._toggleFilters}
            >
              <span>
                <i className="fa fa-search" />{' '}
                {__('search.search.toggle_filters')}
              </span>
            </button>
          </div>
        )}

        {showSearchFilters && (
          <SearchFilters
            params={this.state.params}
            results={this.state.results}
            capacityRanges={this.props.capacityRanges}
            eventTypes={this.props.eventTypes}
            venueServices={this.props.venueServices}
          />
        )}
        {this.state.isLoading && (
          <div className="m-search-fixed-spinner">
            <Spinner text={__('search.search.loading')} />
          </div>
        )}
        <SearchResults
          results={this.state.results}
          activeResultId={this.state.activeResultId}
          highlightCapacitySitting={highlightCapacitySitting}
          openInNewTab={openInNewTab}
          params={this.state.params}
          isLoading={this.state.isLoading}
          features={this.props.features}
        />
        {MapSearch &&
          this.state.isMapboxLoaded &&
          !this.state.isSmallScreen && (
            <MapSearch
              isLoading={this.state.isLoading}
              results={this.state.results}
              activeResultId={this.state.activeResultId}
            />
          )}
      </div>
    );
  }

  _handleResize = () => {
    window.requestAnimationFrame(() => {
      const isSmallScreen = !satisfiesMediaQuery('topbar');

      if (!isSmallScreen) {
        this._lazyLoadMapCode();
      }

      this.setState({ isSmallScreen });
    });
  };

  _lazyLoadMapCode = () => {
    // Bail out if we're already in the process of loading the map related code
    if (this.isLazyLoadingMapCode) return;
    this.isLazyLoadingMapCode = true;

    // Use two splitpoints to try to get the mapbox to a chunk shared between search and
    // venue page, i.e. use two different `import()` calls
    import('jsRoot/setupMapbox').then(() => {
      this.setState({
        isMapboxLoaded: true
      });
    });

    import('search/components/MapSearch')
      .then(mod => mod.default)
      .then(MapSearch => {
        this.setState({
          MapSearch
        });
      });
  };

  /**
   * Event handler for 'change' events coming from the stores
   */
  _onChange = () => {
    this.setState(getStateFromStores());
  };

  _toggleFilters = () => {
    this.setState({
      isFiltersVisible: !this.state.isFiltersVisible
    });
  };
}

export default SearchApp;
